/**
 * Backward-compatible hook that provides the same interface as the old AgentContext.
 * Under the hood, it now uses the new Zustand stores for better performance.
 *
 * For new code, prefer using the stores directly:
 * - useProjectListStore for project list operations
 * - useCurrentProjectStore for current project/chat operations
 */
import { useCallback, useMemo } from 'react';
import { useProjectListStore } from '../stores/projectListStore';
import { useCurrentProjectStore } from '../stores/currentProjectStore';
import type {
  InitiateCasePayload,
  InitiateCaseResponse,
  ProvideFeedbackPayload,
  UpdatePrdPayload,
  UpdateStatusPayload,
  EffortEstimate,
  CostEstimate,
  ValueProjection,
} from '../services/agent/AgentService';
import { HttpAgentAdapter } from '../services/agent/HttpAgentAdapter';
import Logger from '../utils/logger';

const logger = Logger.create('useAgentContext');

// Singleton service instance for operations not yet in stores
const agentService = new HttpAgentAdapter();

export const useAgentContext = () => {
  // Project list store
  const projects = useProjectListStore((s) => s.projects);
  const isLoadingProjects = useProjectListStore((s) => s.isLoading);
  const projectsError = useProjectListStore((s) => s.error);
  const fetchProjects = useProjectListStore((s) => s.fetchProjects);
  const deleteProjectFromStore = useProjectListStore((s) => s.deleteProject);

  // Current project store
  const currentProjectId = useCurrentProjectStore((s) => s.projectId);
  const currentProjectDetails = useCurrentProjectStore((s) => s.projectDetails);
  const messages = useCurrentProjectStore((s) => s.messages);
  const isLoadingDetails = useCurrentProjectStore((s) => s.isLoadingDetails);
  const isLoading = useCurrentProjectStore((s) => s.isLoading);
  const isSendingMessage = useCurrentProjectStore((s) => s.isSendingMessage);
  const error = useCurrentProjectStore((s) => s.error);
  const detailsError = useCurrentProjectStore((s) => s.detailsError);
  const fetchDetails = useCurrentProjectStore((s) => s.fetchDetails);
  const initiateProjectFromStore = useCurrentProjectStore(
    (s) => s.initiateProject
  );
  const sendMessageFromStore = useCurrentProjectStore((s) => s.sendMessage);
  const clearCurrentProject = useCurrentProjectStore(
    (s) => s.clearCurrentProject
  );
  const refreshDetails = useCurrentProjectStore((s) => s.refreshDetails);

  // Backward-compatible aliases
  const fetchUserCases = fetchProjects;
  const fetchCaseDetails = fetchDetails;
  const cases = projects;
  const isLoadingCases = isLoadingProjects;
  const casesError = projectsError;
  const isLoadingCaseDetails = isLoadingDetails;
  const caseDetailsError = detailsError;
  const currentCaseId = currentProjectId;
  const currentCaseDetails = currentProjectDetails;

  // Initiate business case (with project list refresh)
  const initiateBusinessCase = useCallback(
    async (
      payload: InitiateCasePayload
    ): Promise<InitiateCaseResponse | undefined> => {
      const result = await initiateProjectFromStore(payload);
      if (result) {
        // Refresh project list after creating new project
        await fetchProjects();
      }
      return result;
    },
    [initiateProjectFromStore, fetchProjects]
  );

  // Send feedback (message) to agent
  const sendFeedbackToAgent = useCallback(
    async (payload: ProvideFeedbackPayload): Promise<void> => {
      await sendMessageFromStore(payload);
    },
    [sendMessageFromStore]
  );

  // Delete case with refresh
  const deleteCase = useCallback(
    async (caseId: string): Promise<boolean> => {
      const result = await deleteProjectFromStore(caseId);
      if (result && currentProjectId === caseId) {
        clearCurrentProject();
      }
      return result;
    },
    [deleteProjectFromStore, currentProjectId, clearCurrentProject]
  );

  // Helper for inline refresh after mutations
  const inlineRefreshCaseDetails = useCallback(
    async (caseId: string) => {
      if (currentProjectId === caseId) {
        await refreshDetails();
      }
    },
    [currentProjectId, refreshDetails]
  );

  // PRD operations (delegated to service with refresh)
  const updatePrdDraft = useCallback(
    async (payload: UpdatePrdPayload): Promise<boolean> => {
      try {
        await agentService.updatePrd(payload);
        await inlineRefreshCaseDetails(payload.caseId);
        return true;
      } catch (err) {
        logger.error('Failed to update PRD draft:', err);
        return false;
      }
    },
    [inlineRefreshCaseDetails]
  );

  const submitPrdForReview = useCallback(
    async (caseId: string): Promise<boolean> => {
      try {
        await agentService.submitPrdForReview(caseId);
        await inlineRefreshCaseDetails(caseId);
        return true;
      } catch (err) {
        logger.error('Failed to submit PRD for review:', err);
        return false;
      }
    },
    [inlineRefreshCaseDetails]
  );

  const approvePrd = useCallback(
    async (caseId: string): Promise<boolean> => {
      try {
        await agentService.approvePrd(caseId);
        await inlineRefreshCaseDetails(caseId);
        return true;
      } catch (err) {
        logger.error('Failed to approve PRD:', err);
        return false;
      }
    },
    [inlineRefreshCaseDetails]
  );

  const rejectPrd = useCallback(
    async (caseId: string, reason?: string): Promise<boolean> => {
      try {
        await agentService.rejectPrd(caseId, reason);
        await inlineRefreshCaseDetails(caseId);
        return true;
      } catch (err) {
        logger.error('Failed to reject PRD:', err);
        return false;
      }
    },
    [inlineRefreshCaseDetails]
  );

  // System Design operations
  const updateSystemDesign = useCallback(
    async (caseId: string, content: string): Promise<boolean> => {
      try {
        await agentService.updateSystemDesign(caseId, content);
        await inlineRefreshCaseDetails(caseId);
        return true;
      } catch (err) {
        logger.error('Failed to update system design:', err);
        return false;
      }
    },
    [inlineRefreshCaseDetails]
  );

  const submitSystemDesignForReview = useCallback(
    async (caseId: string): Promise<boolean> => {
      try {
        await agentService.submitSystemDesignForReview(caseId);
        await inlineRefreshCaseDetails(caseId);
        return true;
      } catch (err) {
        logger.error('Failed to submit system design for review:', err);
        return false;
      }
    },
    [inlineRefreshCaseDetails]
  );

  const approveSystemDesign = useCallback(
    async (caseId: string): Promise<boolean> => {
      try {
        await agentService.approveSystemDesign(caseId);
        await inlineRefreshCaseDetails(caseId);
        return true;
      } catch (err) {
        logger.error('Failed to approve system design:', err);
        return false;
      }
    },
    [inlineRefreshCaseDetails]
  );

  const rejectSystemDesign = useCallback(
    async (caseId: string, reason?: string): Promise<boolean> => {
      try {
        await agentService.rejectSystemDesign(caseId, reason);
        await inlineRefreshCaseDetails(caseId);
        return true;
      } catch (err) {
        logger.error('Failed to reject system design:', err);
        return false;
      }
    },
    [inlineRefreshCaseDetails]
  );

  // Financial estimate operations
  const updateEffortEstimate = useCallback(
    async (caseId: string, data: EffortEstimate): Promise<boolean> => {
      try {
        await agentService.updateEffortEstimate(caseId, data);
        await inlineRefreshCaseDetails(caseId);
        return true;
      } catch (err) {
        logger.warn('Failed to update effort estimate:', err);
        return false;
      }
    },
    [inlineRefreshCaseDetails]
  );

  const submitEffortEstimateForReview = useCallback(
    async (caseId: string): Promise<boolean> => {
      try {
        await agentService.submitEffortEstimateForReview(caseId);
        await inlineRefreshCaseDetails(caseId);
        return true;
      } catch (err) {
        logger.warn('Failed to submit effort estimate for review:', err);
        return false;
      }
    },
    [inlineRefreshCaseDetails]
  );

  const updateCostEstimate = useCallback(
    async (caseId: string, data: CostEstimate): Promise<boolean> => {
      try {
        await agentService.updateCostEstimate(caseId, data);
        await inlineRefreshCaseDetails(caseId);
        return true;
      } catch (err) {
        logger.warn('Failed to update cost estimate:', err);
        return false;
      }
    },
    [inlineRefreshCaseDetails]
  );

  const submitCostEstimateForReview = useCallback(
    async (caseId: string): Promise<boolean> => {
      try {
        await agentService.submitCostEstimateForReview(caseId);
        await inlineRefreshCaseDetails(caseId);
        return true;
      } catch (err) {
        logger.warn('Failed to submit cost estimate for review:', err);
        return false;
      }
    },
    [inlineRefreshCaseDetails]
  );

  const updateValueProjection = useCallback(
    async (caseId: string, data: ValueProjection): Promise<boolean> => {
      try {
        await agentService.updateValueProjection(caseId, data);
        await inlineRefreshCaseDetails(caseId);
        return true;
      } catch (err) {
        logger.warn('Failed to update value projection:', err);
        return false;
      }
    },
    [inlineRefreshCaseDetails]
  );

  const submitValueProjectionForReview = useCallback(
    async (caseId: string): Promise<boolean> => {
      try {
        await agentService.submitValueProjectionForReview(caseId);
        await inlineRefreshCaseDetails(caseId);
        return true;
      } catch (err) {
        logger.warn('Failed to submit value projection for review:', err);
        return false;
      }
    },
    [inlineRefreshCaseDetails]
  );

  // Approval operations
  const approveEffortEstimate = useCallback(
    async (caseId: string): Promise<boolean> => {
      try {
        await agentService.approveEffortEstimate(caseId);
        await inlineRefreshCaseDetails(caseId);
        return true;
      } catch (err) {
        logger.warn('Failed to approve effort estimate:', err);
        return false;
      }
    },
    [inlineRefreshCaseDetails]
  );

  const rejectEffortEstimate = useCallback(
    async (caseId: string, reason?: string): Promise<boolean> => {
      try {
        await agentService.rejectEffortEstimate(caseId, reason);
        await inlineRefreshCaseDetails(caseId);
        return true;
      } catch (err) {
        logger.warn('Failed to reject effort estimate:', err);
        return false;
      }
    },
    [inlineRefreshCaseDetails]
  );

  const approveCostEstimate = useCallback(
    async (caseId: string): Promise<boolean> => {
      try {
        await agentService.approveCostEstimate(caseId);
        await inlineRefreshCaseDetails(caseId);
        return true;
      } catch (err) {
        logger.warn('Failed to approve cost estimate:', err);
        return false;
      }
    },
    [inlineRefreshCaseDetails]
  );

  const rejectCostEstimate = useCallback(
    async (caseId: string, reason?: string): Promise<boolean> => {
      try {
        await agentService.rejectCostEstimate(caseId, reason);
        await inlineRefreshCaseDetails(caseId);
        return true;
      } catch (err) {
        logger.warn('Failed to reject cost estimate:', err);
        return false;
      }
    },
    [inlineRefreshCaseDetails]
  );

  const approveValueProjection = useCallback(
    async (caseId: string): Promise<boolean> => {
      try {
        await agentService.approveValueProjection(caseId);
        await inlineRefreshCaseDetails(caseId);
        return true;
      } catch (err) {
        logger.warn('Failed to approve value projection:', err);
        return false;
      }
    },
    [inlineRefreshCaseDetails]
  );

  const rejectValueProjection = useCallback(
    async (caseId: string, reason?: string): Promise<boolean> => {
      try {
        await agentService.rejectValueProjection(caseId, reason);
        await inlineRefreshCaseDetails(caseId);
        return true;
      } catch (err) {
        logger.warn('Failed to reject value projection:', err);
        return false;
      }
    },
    [inlineRefreshCaseDetails]
  );

  // Final approval operations
  const submitCaseForFinalApproval = useCallback(
    async (caseId: string): Promise<boolean> => {
      try {
        await agentService.submitCaseForFinalApproval(caseId);
        await inlineRefreshCaseDetails(caseId);
        return true;
      } catch (err) {
        logger.warn('Failed to submit case for final approval:', err);
        return false;
      }
    },
    [inlineRefreshCaseDetails]
  );

  const approveFinalCase = useCallback(
    async (caseId: string): Promise<boolean> => {
      try {
        await agentService.approveFinalCase(caseId);
        await inlineRefreshCaseDetails(caseId);
        return true;
      } catch (err) {
        logger.warn('Failed to approve final case:', err);
        return false;
      }
    },
    [inlineRefreshCaseDetails]
  );

  const rejectFinalCase = useCallback(
    async (caseId: string, reason?: string): Promise<boolean> => {
      try {
        await agentService.rejectFinalCase(caseId, reason);
        await inlineRefreshCaseDetails(caseId);
        return true;
      } catch (err) {
        logger.warn('Failed to reject final case:', err);
        return false;
      }
    },
    [inlineRefreshCaseDetails]
  );

  // Status update
  const updateStatus = useCallback(
    async (payload: UpdateStatusPayload): Promise<boolean> => {
      try {
        await agentService.updateStatus(payload);
        await inlineRefreshCaseDetails(payload.caseId);
        return true;
      } catch (err) {
        logger.error('Failed to update status:', err);
        return false;
      }
    },
    [inlineRefreshCaseDetails]
  );

  // Export to PDF
  const exportCaseToPdf = useCallback(async (caseId: string): Promise<void> => {
    try {
      const blob = await agentService.exportCaseToPdf(caseId);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `business_case_${caseId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      logger.error('Failed to export case to PDF:', err);
      throw err;
    }
  }, []);

  // Utility functions
  const clearAgentState = useCallback(() => {
    useProjectListStore.getState().reset();
    useCurrentProjectStore.getState().reset();
  }, []);

  const clearCurrentCaseDetails = clearCurrentProject;

  const clearError = useCallback(
    (errorType?: 'general' | 'cases' | 'caseDetails') => {
      if (errorType === 'cases' || !errorType) {
        useProjectListStore.getState().clearError();
      }
      if (
        errorType === 'general' ||
        errorType === 'caseDetails' ||
        !errorType
      ) {
        useCurrentProjectStore.getState().clearError();
      }
    },
    []
  );

  // Memoize the return value to prevent unnecessary re-renders
  return useMemo(
    () => ({
      // State
      currentCaseId,
      messages,
      cases,
      currentCaseDetails,
      isLoading: isLoading || isSendingMessage,
      isLoadingCases,
      isLoadingCaseDetails,
      error,
      casesError,
      caseDetailsError,

      // Actions
      initiateBusinessCase,
      sendFeedbackToAgent,
      fetchUserCases,
      fetchCaseDetails,
      updatePrdDraft,
      updateStatus,
      submitPrdForReview,
      approvePrd,
      rejectPrd,
      updateSystemDesign,
      submitSystemDesignForReview,
      approveSystemDesign,
      rejectSystemDesign,
      updateEffortEstimate,
      submitEffortEstimateForReview,
      updateCostEstimate,
      submitCostEstimateForReview,
      updateValueProjection,
      submitValueProjectionForReview,
      approveEffortEstimate,
      rejectEffortEstimate,
      approveCostEstimate,
      rejectCostEstimate,
      approveValueProjection,
      rejectValueProjection,
      submitCaseForFinalApproval,
      approveFinalCase,
      rejectFinalCase,
      exportCaseToPdf,
      deleteCase,
      clearAgentState,
      clearCurrentCaseDetails,
      clearError,
    }),
    [
      currentCaseId,
      messages,
      cases,
      currentCaseDetails,
      isLoading,
      isSendingMessage,
      isLoadingCases,
      isLoadingCaseDetails,
      error,
      casesError,
      caseDetailsError,
      initiateBusinessCase,
      sendFeedbackToAgent,
      fetchUserCases,
      fetchCaseDetails,
      updatePrdDraft,
      updateStatus,
      submitPrdForReview,
      approvePrd,
      rejectPrd,
      updateSystemDesign,
      submitSystemDesignForReview,
      approveSystemDesign,
      rejectSystemDesign,
      updateEffortEstimate,
      submitEffortEstimateForReview,
      updateCostEstimate,
      submitCostEstimateForReview,
      updateValueProjection,
      submitValueProjectionForReview,
      approveEffortEstimate,
      rejectEffortEstimate,
      approveCostEstimate,
      rejectCostEstimate,
      approveValueProjection,
      rejectValueProjection,
      submitCaseForFinalApproval,
      approveFinalCase,
      rejectFinalCase,
      exportCaseToPdf,
      deleteCase,
      clearAgentState,
      clearCurrentCaseDetails,
      clearError,
    ]
  );
};
